import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

import type { ApiError, Envelope } from "@/types/api";
import { IDLE_SIGN_OUT_MESSAGE, announceIdleSignOut, isIdleTimeoutError } from "@/lib/session/idleSession";
import { announceRevokedSignOut } from "@/lib/session/revokedSession";
import { useAuthStore } from "@/stores/auth-store";
import { notify } from "@/lib/notify";

/**
 * A typed error thrown by every API call in this app. Components read
 * `.appError` for the structured `{code, message, fields}` shape rather than
 * poking at axios internals - see ui/ErrorMessage and forms' field mapping.
 */
export class ApiRequestError extends Error {
  appError: ApiError;
  status?: number;
  requestId?: string;

  constructor(appError: ApiError, status?: number, requestId?: string) {
    super(appError.message);
    this.name = "ApiRequestError";
    this.appError = appError;
    this.status = status;
    this.requestId = requestId;
  }
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 70_000, // slightly above the backend's 60s deviceSyncTimeout so a slow LAN pull never client-aborts right at the server deadline
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // A Super Admin who has opened a workspace from the platform Tenants list
  // has no tenant of their own in the JWT, so the chosen tenant travels as a
  // header instead. The backend only honours this for a super-admin token
  // (internal/transport/http/middleware/auth.go) - it is not a general
  // tenant-switching mechanism.
  const impersonatedTenant = useAuthStore.getState().impersonatedTenant;
  if (impersonatedTenant) {
    config.headers["X-Tenant-ID"] = impersonatedTenant.id;
  }
  // Multi-Branch Tenant Admin: the session-scoped active branch travels as a
  // header the same way, honoured server-side only for role "tenant_admin"
  // (establish()'s X-Branch-ID block). A purely additive view filter - every
  // other role already only ever sees what it's permitted to, with or
  // without this header.
  const { user, activeBranch } = useAuthStore.getState();
  if (activeBranch && user?.role === "tenant_admin") {
    config.headers["X-Branch-ID"] = activeBranch.id;
  }
  return config;
});

/** One classified failure, shaped for ApiRequestError's constructor. */
export interface ClassifiedError {
  appError: ApiError;
  status?: number;
  requestId?: string;
}

/**
 * Classifies a failed response into the ApiError shape getErrorMessage
 * expects - pulled out of the response interceptor below so this decision
 * (timeout vs offline vs a real HTTP response vs no response reaching the
 * server at all) is directly unit-testable without mocking axios's
 * transport layer. Does not handle the 401-refresh-and-retry path (the
 * interceptor handles that separately - it's stateful/async in a way this
 * pure function deliberately isn't).
 */
export function classifyResponseError(
  error: { code?: string; response?: { status?: number; data?: Envelope<unknown> } },
  isOnline: boolean,
): ClassifiedError {
  // Neither a timeout nor an offline network error ever reaches the server,
  // so `error.response` is undefined for both - that absence is exactly
  // what distinguishes them from a normal HTTP error response below.
  if (error.code === "ECONNABORTED") {
    return { appError: { code: "TIMEOUT", message: "" } };
  }
  if (!error.response && !isOnline) {
    return { appError: { code: "NETWORK_OFFLINE", message: "" } };
  }

  const status = error.response?.status;
  const body = error.response?.data;
  if (status) {
    // A real HTTP response came back but its body isn't the API's JSON
    // envelope (no `body.error`) - e.g. a proxy/load balancer's own HTML
    // error page in front of a 502/503, or anything else ahead of the Go
    // backend. Deliberately leaving `message` empty here rather than
    // axios's own generic `error.message` ("Request failed with status
    // code 503") - that string is not written for a human, and stuffing it
    // in here would permanently shadow getErrorMessage's status-specific
    // friendly fallback (errorMessage.ts's STATUS_FALLBACK), which only
    // applies when `message` is falsy. A body that DOES parse as the
    // envelope keeps its own message untouched, preserving whatever safe,
    // specific business message the backend chose to send.
    const appError: ApiError = body?.error ?? { code: "HTTP_ERROR", message: "" };
    return { appError, status, requestId: body?.request_id };
  }

  // No response at all, and neither of the more specific checks above
  // matched (not a timeout, not the browser reporting itself offline) - the
  // request never reached the server: DNS failure, connection refused, a
  // CORS rejection, etc. Distinct from NETWORK_OFFLINE (the browser itself
  // has no connection) and from a real HTTP error (the server responded,
  // just unhappily).
  return { appError: { code: "CONNECTION_FAILED", message: "" } };
}

/**
 * Whether a failure WHILE refreshing the access token should end the
 * session (sign out) or is merely transient (a network/server hiccup
 * during the refresh call itself). Only an explicit 401 from POST
 * /auth/refresh - or having no refresh token at all to even try - means
 * the refresh credential was actually rejected; a timeout, offline state,
 * or a 5xx from the refresh endpoint is not that, and must not be treated
 * as one, or a dropped connection during a background refresh would sign
 * someone out of a session that was never actually invalid.
 */
export function refreshFailureIsFatal(refreshError: unknown): boolean {
  const noRefreshToken = refreshError instanceof Error && refreshError.message === "no refresh token available";
  const refreshRejected = axios.isAxiosError(refreshError) && refreshError.response?.status === 401;
  return noRefreshToken || refreshRejected;
}

// A single in-flight refresh is shared by every request that races into a 401,
// so a page that fires ten queries at once triggers one refresh, not ten.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) throw new Error("no refresh token available");

  const response = await axios.post<Envelope<{ access_token: string; expires_at: string }>>(
    `${import.meta.env.VITE_API_BASE_URL}/auth/refresh`,
    { refresh_token: refreshToken },
  );
  const token = response.data.data?.access_token;
  if (!token) throw new Error("refresh response carried no access token");

  useAuthStore.getState().setAccessToken(token);
  void syncPermissionsAfterRefresh(token);
  return token;
}

// A refresh is also how an access change (an additional role granted, removed
// or expired) reaches this session - the backend answers the stale token with
// a refreshable 401. The new token is already authoritative for the API; this
// pulls the same effective permission list into the store so menus, routes and
// buttons follow it too. Best-effort: a failure leaves the previous list until
// the next refresh, and the server still enforces everything regardless.
async function syncPermissionsAfterRefresh(token: string): Promise<void> {
  try {
    const response = await axios.get<Envelope<{ permissions?: string[] }>>(
      `${import.meta.env.VITE_API_BASE_URL}/auth/me`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const permissions = response.data.data?.permissions;
    if (Array.isArray(permissions)) {
      const current = useAuthStore.getState().user?.permissions ?? [];
      const changed = permissions.length !== current.length || permissions.some((p) => !current.includes(p));
      if (changed) useAuthStore.getState().setPermissions(permissions);
    }
  } catch {
    // see above
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<Envelope<unknown>>) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined;
    const status = error.response?.status;

    // A 401 on a request that isn't the login/refresh call itself gets one
    // retry after refreshing. Anything past that is a real sign-out.
    const isAuthEndpoint = original?.url?.includes("/auth/login") || original?.url?.includes("/auth/refresh");

    if (status === 401 && original && !original._retried && !isAuthEndpoint) {
      original._retried = true;
      let token: string;
      try {
        refreshPromise ??= refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
        token = await refreshPromise;
      } catch (refreshError) {
        if (refreshFailureIsFatal(refreshError)) {
          // The toast fires here, directly, rather than relying on the global
          // mutation-error handler: the request that surfaces a dead session
          // is just as often a background query (loading a list page) as a
          // mutation, and the mutation-only handler would silently miss it.
          // signOut() clears the token and ProtectedRoute reactively redirects
          // to /login on the next render - no window.location.assign, since a
          // hard reload would tear down the toast before it's ever seen.
          // The original request carries the reason when the idle check ended the
          // session there (refresh then finds the session already gone).
          if (isIdleTimeoutError(refreshError) || isIdleTimeoutError(error)) {
            // Tell the other tabs of this browser, which share the idle clock.
            announceIdleSignOut();
            notify.error(IDLE_SIGN_OUT_MESSAGE);
          } else {
            // Tell this browser's other tabs of the same session, so they do
            // not each wait for their own failing request.
            announceRevokedSignOut(useAuthStore.getState().accessToken);
            notify.error("Session expired. Please log in again.");
          }
          useAuthStore.getState().signOut();
          return Promise.reject(
            new ApiRequestError({ code: "SESSION_EXPIRED", message: "the access token has expired" }, 401),
          );
        }

        // Transient - leave the session intact. Reject just this one request
        // with a normal, retryable error; the caller's usual error handling
        // (global mutation toast, inline ErrorMessage, a query's own retry)
        // takes it from here. Bounded by the same mechanisms that bound any
        // other failure - TanStack Query's default single retry for queries,
        // no retry for mutations (lib/query-client.ts) - so this can't loop:
        // `original._retried` is already set, so even a further 401 on retry
        // falls through to an ordinary error below rather than refreshing again.
        return Promise.reject(new ApiRequestError({ code: "SESSION_REFRESH_FAILED", message: "" }));
      }
      original.headers.Authorization = `Bearer ${token}`;
      return api.request(original);
    }

    await readBlobErrorBody(error);
    const { appError, status: classifiedStatus, requestId } = classifyResponseError(error, navigator.onLine);
    // The session was signed in with an admin-issued temporary password and
    // the server refuses everything but /auth until it is changed. Flag the
    // user so ProtectedRoute swaps the app for the "set a new password" screen
    // (normally the login response already carries the flag; this covers a
    // state that went stale, e.g. a reset done while this tab was open).
    if (appError.code === "PASSWORD_CHANGE_REQUIRED") {
      const { user, setUser } = useAuthStore.getState();
      if (user && !user.must_change_password) setUser({ ...user, must_change_password: true });
    }
    return Promise.reject(new ApiRequestError(appError, classifiedStatus, requestId));
  },
);

/**
 * A download (responseType "blob") receives its JSON error envelope as a Blob,
 * so the server's own message ("your role does not grant report export") was
 * lost and the user saw a generic one. Read the Blob back into the envelope
 * in place, before classification. Anything that is not a JSON envelope is
 * left untouched.
 */
export async function readBlobErrorBody(error: { response?: { data?: unknown } }): Promise<void> {
  const body = error.response?.data;
  if (typeof Blob === "undefined" || !(body instanceof Blob) || !error.response) return;
  try {
    const parsed: unknown = JSON.parse(await body.text());
    if (parsed && typeof parsed === "object") error.response.data = parsed;
  } catch {
    // Not JSON (e.g. an HTML proxy error page) - keep the generic handling.
  }
}

/** Unwraps the envelope, throwing ApiRequestError if the call failed. */
export async function unwrap<T>(promise: Promise<{ status: number; data: Envelope<T> }>): Promise<T> {
  const { status, data: envelope } = await promise;
  // A 204 has no body at all - httpx.NoContent (used by delete/restore/etc)
  // writes only the status, no JSON. Axios hands back an empty string for
  // `data` in that case, which is not the {success, data} envelope every
  // other response carries - treating that as "malformed" (as this used to)
  // turned every legitimate no-content success into "The server returned no
  // data." An endpoint that instead answers 200/201 with a real envelope
  // (including one whose `data` is deliberately absent) still goes through
  // the normal validation below unchanged, so a genuinely broken response is
  // still caught.
  if (status === 204) {
    return undefined as T;
  }
  if (!envelope?.success || envelope.data === undefined) {
    throw new ApiRequestError(
      envelope?.error ?? { code: "UNKNOWN", message: "The server returned no data." },
      undefined,
      envelope?.request_id,
    );
  }
  return envelope.data;
}

/** Unwraps a paginated envelope into the {items, meta} shape components expect. */
export async function unwrapPage<T>(
  promise: Promise<{ status: number; data: Envelope<T[]> }>,
): Promise<{ items: T[]; meta: Envelope<T[]>["meta"] }> {
  const { status, data: envelope } = await promise;
  if (status === 204) {
    return { items: [], meta: undefined };
  }
  if (!envelope?.success) {
    throw new ApiRequestError(
      envelope?.error ?? { code: "UNKNOWN", message: "The server returned no data." },
      undefined,
      envelope?.request_id,
    );
  }
  return { items: envelope.data ?? [], meta: envelope.meta };
}

// ---------------------------------------------------------------- shortcuts
//
// Every domain module (lib/api/employees.ts etc.) calls these instead of
// wiring axios + unwrap by hand, so a resource module reads as a plain list of
// endpoints with no envelope boilerplate repeated at every call site.

// Every domain filter type (EmployeeFilters, AttendanceFilters, ...) is a
// plain object of primitives, but each is its own named interface rather than
// an indexable Record - which is what makes autocomplete on filter fields
// work at call sites. `object` here just asks for "some serialisable query
// object", so those interfaces satisfy it without needing an index signature.
function toParams(query?: object) {
  if (!query) return undefined;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  return params;
}

export const http = {
  get: <T>(url: string, query?: object) => unwrap(api.get<Envelope<T>>(url, { params: toParams(query) })),

  getPage: <T>(url: string, query?: object) =>
    unwrapPage(api.get<Envelope<T[]>>(url, { params: toParams(query) })),

  post: <T>(url: string, body?: unknown) => unwrap(api.post<Envelope<T>>(url, body)),

  put: <T>(url: string, body?: unknown) => unwrap(api.put<Envelope<T>>(url, body)),

  patch: <T>(url: string, body?: unknown) => unwrap(api.patch<Envelope<T>>(url, body)),

  delete: <T>(url: string, body?: unknown) => unwrap(api.delete<Envelope<T>>(url, { data: body })),

  /** Downloads a binary/CSV response as a Blob for save-as, bypassing the JSON envelope. */
  download: async (url: string, query?: object): Promise<Blob> => {
    const response = await api.get(url, { params: toParams(query), responseType: "blob" });
    return response.data as Blob;
  },

  /** POSTs a body and returns the binary response (e.g. a generated PDF) as a Blob. */
  postDownload: async (url: string, body?: unknown): Promise<Blob> => {
    const response = await api.post(url, body, { responseType: "blob" });
    return response.data as Blob;
  },
};
